import {
  Alert,
  Box,
  Button,
  Form,
  FormField,
  Modal,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  ContentEngagement,
  ContentEngagementType,
} from '@amzn/awscat-pattern-library-service-model';
import constants from '../../common/constants';
import { ContentEngagementDisplayProps, ContentEngagementList } from './ContentEngagementList';
import { ContentEngagementUpdateProps } from './ContentEngagementList';

interface ContentEngagementModalProps {
  engagements: ContentEngagement[];
  onCancel?: () => void;
  onUpdate?: (updatedEngagements: ContentEngagement[]) => void;
  visible?: boolean;
  loading?: boolean;
  /** A load/save failure to surface inline at the top of the modal. */
  errorMessage?: string;
  /** Whether saving is allowed; when false the Confirm button stays disabled. */
  canSave?: boolean;
}

const ContentEngagementModal: React.FunctionComponent<ContentEngagementModalProps> = (
  props: ContentEngagementModalProps,
) => {
  const {
    engagements,
    loading = false,
    onCancel,
    onUpdate,
    visible = true,
    errorMessage,
    canSave = true,
  } = props;
  const [userEngagements, setUserEngagements] = useState([]);
  const [formError, setFormError] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');

  const initializeState = () => {
    if (engagements?.length === 0) {
      setUserEngagements([
        // @ts-expect-error TS2322 - strict migration
        {
          engagementType: ContentEngagementType.Project,
          salesforceId: '',
          notes: '',
          key: uuid(),
          salesforceIdError: constants.CONTENT_ENGAGEMENT_MISSING_SALESFORCE_PROJECT,
        },
      ]);
      setFormError(true);
    } else {
      setUserEngagements(
        // @ts-expect-error TS2345 - strict migration
        engagements.map((item) => ({ ...item, key: item.salesforceId, salesforceIdError: '' })),
      );
    }
    setDuplicateError('');
  };

  useEffect(() => {
    initializeState();
  }, [props.engagements]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirm = () => {
    if (onUpdate) {
      onUpdate(userEngagements);
    }
  };
  const onDismiss = () => {
    initializeState();
    if (onCancel) {
      onCancel();
    }
  };

  const checkDuplicates = (
    updatedEngagements: Array<ContentEngagement & ContentEngagementDisplayProps>,
  ) => {
    const filteredItems = updatedEngagements
      .map((item) => item.salesforceId)
      .filter((item) => item !== '');
    const distinctItems = [...new Set(filteredItems)];
    if (distinctItems.length < filteredItems.length) {
      return `${constants.CONTENT_ENGAGEMENT_DUPLICATE_SALESFORCE_URL}`;
    }
    return '';
  };

  const updateFormError = (
    updatedEngagements: Array<ContentEngagement & ContentEngagementDisplayProps>,
  ) => {
    const updatedFormError = updatedEngagements.some((item) => item.salesforceIdError);
    const dupError = checkDuplicates(updatedEngagements);
    setDuplicateError(dupError);
    setFormError(updatedFormError || !!dupError);
  };

  const onItemAdd = () => {
    const updatedEngagements = [...userEngagements];
    // @ts-expect-error TS2345 - strict migration
    updatedEngagements.push({
      engagementType: ContentEngagementType.Project,
      salesforceId: '',
      notes: '',
      key: uuid(),
      salesforceIdError: constants.CONTENT_ENGAGEMENT_MISSING_SALESFORCE_PROJECT,
    });
    setUserEngagements(updatedEngagements);
    updateFormError(updatedEngagements);
  };

  const onItemUpdate = (
    engagementIndex: number,
    { salesforceId, notes, engagementType, validationError }: ContentEngagementUpdateProps,
  ) => {
    const updatedEngagements = [...userEngagements];
    // @ts-expect-error TS2322 - strict migration
    updatedEngagements[engagementIndex] = {
      // @ts-expect-error TS2698 - strict migration
      ...updatedEngagements[engagementIndex],
      engagementType,
      salesforceId,
      notes,
      salesforceIdError: validationError,
    };
    setUserEngagements(updatedEngagements);
    updateFormError(updatedEngagements);
  };

  const onItemDelete = (engagementIndex: number) => {
    const updatedEngagements = [...userEngagements];
    updatedEngagements.splice(engagementIndex, 1);
    setUserEngagements(updatedEngagements);
    updateFormError(updatedEngagements);
  };

  const formActions = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={onDismiss} variant="link">
          {constants.CANCEL_BUTTON_TEXT}
        </Button>
        <Button onClick={onItemAdd} variant="normal">
          {constants.ADD_ENGAGEMENT_BUTTON_TEXT}
        </Button>
        <Button onClick={onConfirm} disabled={formError || !canSave} variant="primary">
          {constants.CONFIRM_BUTTON_TEXT}
        </Button>
      </SpaceBetween>
    </Box>
  );

  if (!visible) {
    return null;
  }

  if (loading) {
    return <Spinner size="normal" />;
  }

  return (
    <div className="form-action">
      <Modal
        className="awsui"
        onDismiss={onDismiss}
        header={constants.CONTENT_ENGAGEMENT_MODAL_TITLE}
        size="large"
        visible
        closeAriaLabel="Close modal"
      >
        <Form actions={formActions}>
          {errorMessage !== undefined && (
            <Box margin={{ bottom: 'm' }}>
              <Alert type="error">{errorMessage}</Alert>
            </Box>
          )}
          <Box variant="p" margin={{ bottom: 'm' }}>
            {constants.CONTENT_ENGAGEMENT_MODAL_HEADER}
          </Box>
          <Box variant="p" margin={{ bottom: 'm' }}>
            {constants.CONTENT_ENGAGEMENT_MODAL_MESSAGE}
          </Box>
          <ContentEngagementList
            engagements={userEngagements}
            onItemUpdate={onItemUpdate}
            onItemDelete={onItemDelete}
          />
          <Box float="left">
            <FormField
              label=""
              errorText={duplicateError}
              stretch
              i18nStrings={{ errorIconAriaLabel: 'Error' }}
            />
          </Box>
        </Form>
      </Modal>
    </div>
  );
};

export default ContentEngagementModal;
